import { DebugLogger } from '@pique/debug';
// @ts-expect-error upstream type is wrong
import { createKeybindingsHandler } from 'tinykeys';

import type { PiqueCommand, PiqueCommandOptions } from './command';
import { createPiqueCommand } from './command';

const commandLogger = new DebugLogger('command:registry');

interface KeyBindingMap {
  [keybinding: string]: (event: KeyboardEvent) => void;
}

export interface KeyBindingOptions {
  /**
   * Key presses will listen to this event (default: "keydown").
   */
  event?: 'keydown' | 'keyup';

  /**
   * Whether to capture the event during the capture phase (default: false).
   */
  capture?: boolean;

  /**
   * Keybinding sequences will wait this long between key presses before
   * cancelling (default: 1000).
   *
   * **Note:** Setting this value too low (i.e. `300`) will be too fast for many
   * of your users.
   */
  timeout?: number;
}

const bindKeys = (
  target: Window | HTMLElement,
  keyBindingMap: KeyBindingMap,
  options: KeyBindingOptions = {}
) => {
  const event = options.event ?? 'keydown';
  const onKeyEvent = createKeybindingsHandler(keyBindingMap, options);
  target.addEventListener(event, onKeyEvent, options.capture);
  return () => {
    target.removeEventListener(event, onKeyEvent, options.capture);
  };
};

export const PiqueCommandRegistry = new (class {
  readonly commands: Map<string, PiqueCommand> = new Map();
  private readonly cleanupMap: Map<string, () => void> = new Map();

  register(options: PiqueCommandOptions) {
    if (this.commands.has(options.id)) {
      // Silently return existing cleanup function for duplicate registrations
      // This is common during development hot reloads
      return this.cleanupMap.get(options.id) || (() => { });
    }
    const command = createPiqueCommand(options);
    this.commands.set(command.id, command);

    let unsubKb: (() => void) | undefined;

    if (
      command.keyBinding &&
      !command.keyBinding.skipRegister &&
      typeof window !== 'undefined'
    ) {
      const { binding: keybinding, capture } = command.keyBinding;
      unsubKb = bindKeys(
        window,
        {
          [keybinding]: (e: Event) => {
            e.preventDefault();
            command.run()?.catch(e => {
              console.error(`Failed to run command [${command.id}]`, e);
            });
          },
        },
        {
          capture,
        }
      );
    }

    const cleanup = () => {
      unsubKb?.();
      this.commands.delete(command.id);
      this.cleanupMap.delete(command.id);
    };

    this.cleanupMap.set(command.id, cleanup);
    return cleanup;
  }

  get(id: string): PiqueCommand | undefined {
    if (!this.commands.has(id)) {
      commandLogger.warn(`Command ${id} not registered.`);
      return undefined;
    }
    return this.commands.get(id);
  }

  getAll(): PiqueCommand[] {
    return Array.from(this.commands.values());
  }
})();

export function registerPiqueCommand(options: PiqueCommandOptions) {
  return PiqueCommandRegistry.register(options);
}
